<!-- This component is used to display the complete step. Each plan holder has a button to display a preview of their merged document.-->
<template>
	<div class="relative">
		<div class="flex flex-col gap-4 px-20 pt-24 font-sans">
			<div
				v-for="(planHolder, index) in sessionStore.activePlans"
				:key="planHolder.id"
			>
				<component
					:is="
						reviewComponent(
							planHolder.packetStatus === PlanPacketStatus.Submitted
						)
					"
					:planHolder="planHolder"
					:foxitReady="foxitReady"
					:index="index"
					:emailError="getEmailError(planHolder, index)"
					@preview="openPreview(planHolder.id, index)"
					@updateErrors="updatedEmailErrors(planHolder, index)"
				/>
			</div>
		</div>
		<Teleport
			defer
			to="#main-content"
		>
			<InlineModal
				v-show="open"
				v-model:open="open"
			>
				<template #header>
					<div class="mb-4 flex w-full items-center gap-3">
						<button
							type="button"
							@click="open = false"
						>
							<ArrowLongLeftIcon class="h-6 w-6" />
						</button>
						<span class="flex items-center gap-3">
							<PlanAvatar
								:plan="previewPlanHolder"
								:index="previewIndex"
								:filled="true"
								:large="true"
							/>
							<span class="text-2xl font-bold">{{
								t('document.previewTitle', {
									name: previewPlanHolder?.firstName,
								})
							}}</span>
						</span>
					</div>
				</template>
				<FoxItPreview
					v-model:foxitReady="foxitReady"
					v-model:open="open"
					:planHolder="previewPlanHolder"
				/>
			</InlineModal>
		</Teleport>
	</div>
</template>

<script setup lang="ts">
import FoxItPreview from './FoxitPreview.vue'
import PlanHolderPreview from './PlanHolderPreview.vue'
import PlanHolderSuccess from './PlanHolderSuccess.vue'
import Plan from '@/classes/Plan'
import { PlanErrorType } from '@/classes/Plan'
import InlineModal from '@/components/common/InlineModal.vue'
import PlanAvatar from '@/components/presentation/PlanAvatar.vue'
import { PlanPacketStatus } from '@/enums/PlanPacketStatus'
import { usePresentationLocale } from '@/i18n'
import { usePlanStore } from '@/stores/plan'
import { useSessionStore } from '@/stores/session'
import { ArrowLongLeftIcon } from '@heroicons/vue/24/outline'
import { ref } from 'vue'

const sessionStore = useSessionStore()
const open = ref(false)
const previewPlanHolder = ref()
const previewIndex = ref(0)
const { t } = usePresentationLocale()
const planStore = usePlanStore()

// This method is called to display a preview of a planHolder's merged packet document
const openPreview = (planHolderId: string, index: number) => {
	previewPlanHolder.value = sessionStore.getPlanHolder(planHolderId)
	previewIndex.value = index
	open.value = true
}
const foxitReady = ref(false)

// This method determines whether user should be shown the congratulatory message after submission, or a list of their packet documents
const reviewComponent = (confirmed: boolean) =>
	confirmed ? PlanHolderSuccess : PlanHolderPreview

// This method determines whether the duplicate email check should be skipped based on:
// - No plans with everplans premium and the current plan is the first plan
// - The current plan is the first plan with everplans premium
const canSkipDuplicateEmailCheck = (index: number) => {
	const firstPlanWithEverPlansPremiumIndex = sessionStore.activePlans.findIndex(
		(plan: Plan) => plan.everplansIncluded
	)

	const hasNoPremiumAndIsFirstPlan =
		firstPlanWithEverPlansPremiumIndex === -1 && index === 0
	const isFirstPremiumPlan = index === firstPlanWithEverPlansPremiumIndex

	return hasNoPremiumAndIsFirstPlan || isFirstPremiumPlan
}

const getEmailError = (planHolder: Plan, index: number) => {
	if (!planHolder.email) {
		return {
			type: PlanErrorType.PolicyEmail,
			title: t('document.missingEmail'),
			message: t('document.missingPolicyEmailMessage'),
		}
	}

	if (canSkipDuplicateEmailCheck(index)) {
		return null
	}

	// Only a Premium is missing something when its Everplans email is blank. A Free Everplans
	// can be enrolled without one and have it added later, which is what UpdateEmailModal tells
	// the AFP - warning here as well would contradict it.
	if (!planHolder.everplansEmail && planHolder.isEverplansPremium()) {
		return {
			type: PlanErrorType.EverplansEmail,
			title: t('document.missingEmail'),
			message: t('document.missingEverplansEmailMessage'),
		}
	}

	// Flagged regardless of whether this plan holder has Everplans premium in their cart,
	// which is the desired behaviour as of 08-13-26 standup discussion. Shares its definition
	// of a duplicate with EverplansStep and UpdateEmailModal so the three cannot disagree.
	const plansClaimingThisEmail =
		planStore.findPlansClaimingEverplansEmail(planHolder)

	if (plansClaimingThisEmail.length > 0) {
		return {
			type: PlanErrorType.EverplansEmail,
			title: t('document.duplicateEmail'),
			message: t('document.duplicateEverplansEmailMessage'),
		}
	}

	return null
}

const updatedEmailErrors = async (planHolder: Plan, index: number) => {
	// Reset email related errors on plan
	planHolder.errors = planHolder.errors.filter(
		(e) =>
			![PlanErrorType.PolicyEmail, PlanErrorType.EverplansEmail].includes(
				e.type
			)
	)

	// Add any email errors if they exist
	const error = getEmailError(planHolder, index)

	if (error) {
		planHolder.errors.push(error)
	}

	// Make sure plan has all emails and errors updated
	await planStore.updatePlan(planHolder.id, {
		errors: planHolder.errors,
		email: planHolder.email,
		everplansEmail: planHolder.everplansEmail,
	})
}
</script>
